import { Button } from '@invent/wl-ui-kit-next/ui';

import type { RegistrationCaller, RegistrationSurveyCode } from '../types';
import { startFailure } from './choose-registration';
import { StepHeading, useStepAction } from './step-parts';

/**
 * Where a run that started on a survey is sent, when it is sent anywhere — a mirror of
 * survey-api's `RegistrationSurveys`: RIA_FIRM's `NoMatch` (the RIA door, SR-416's B5-12) and
 * TEAMS' `RiaRedirect` (its route step). Each survey has at most one way out, so the survey a run
 * started on says where a redirected one went; read only where survey-api's own answer is not to
 * hand — a tab come back to a redirected run, whose read is a 409 naming no destination.
 */
const REDIRECT_OF_SURVEY: Readonly<Record<string, RegistrationSurveyCode>> = {
  RIA_FIRM: 'TEAMS',
  TEAMS: 'RIA_FIRM'
};

/** The survey a run started on `startedOn` is sent to when redirected — null where none was
 *  kept, or the survey has no way out. For a run read back, whose 409 names no destination. */
export function redirectOf(startedOn: Nullable<string>): Nullable<string> {
  if (startedOn === null) return null;
  return REDIRECT_OF_SURVEY[startedOn] ?? null;
}

/** What a redirect made — nothing — in the caller's words: no account anonymously, nothing
 *  added signed in. The onboarding study's redirected screen says it too. */
export function redirectedNothing(signedIn: boolean): string {
  return signedIn
    ? 'Nothing was created here: nothing was added to your account.'
    : 'Nothing was created here: no account was made, so there is nothing to sign in to yet.';
}

/**
 * `redirected` — a run survey-api sent to another survey's registration, ending it as it
 * answered, with nothing created: no account, no team, no firm. Two ways in, one per survey that
 * has one (`REDIRECT_OF_SURVEY`): a team whose answers make it an RIA firm (its route step), and
 * an RIA firm whose SEC number the SEC does not list, which asked for the team survey instead
 * (the anchor's "Register as a team instead").
 *
 * **The way on is a new run** (the teams plan's R6), started in the same mode from its first
 * step: nothing typed here carries into it, because nothing here was the other survey's. A
 * start that fails is said as the chooser says one (`startFailure`): it is the same request.
 *
 * The same screen answers a tab that comes back to a redirected run — survey-api then answers
 * `registration-out-of-step` with `step: 'redirected'` and the run `abandoned`, and names no
 * destination, so the page passes the one its kept survey gives (`redirectOf`). Where
 * neither is known — a run stored by a build that kept no survey — the screen says the
 * registration moved and offers the chooser. Signed in, "nothing was created" is about the
 * account: nothing was added to it, and the next run is bound to it in turn.
 */
export function RedirectedStep({
  caller,
  destination,
  onRegister,
  onChoose
}: {
  caller: RegistrationCaller;
  destination: Nullable<string>;
  onRegister: (survey: RegistrationSurveyCode) => Promise<void>;
  onChoose: () => void;
}) {
  const { pending, error, run } = useStepAction();
  const nothing = redirectedNothing(caller === 'account');

  const words = destination === null ? undefined : REDIRECTED_WORDS[destination];
  if (!words) {
    return (
      <>
        <StepHeading title="This registration moved to another survey" lede={nothing} />
        <Button className="w-full" onClick={onChoose}>
          Choose a registration
        </Button>
      </>
    );
  }

  return (
    <>
      <StepHeading title={words.title} lede={`${words.lede}${nothing}`} />

      {error !== null && (
        <p role="alert" className="mb-4 text-sm font-medium text-destructive">
          {startFailure(error)}
        </p>
      )}

      <Button
        className="w-full"
        disabled={pending}
        onClick={() => void run(() => onRegister(words.survey))}
      >
        {pending ? 'Starting…' : words.press}
      </Button>
    </>
  );
}

/** What the screen says, by where the run was sent — the two surveys a run is redirected to.
 *  The onboarding study's redirected screen says the same, through the barrel. */
export const REDIRECTED_WORDS: Readonly<
  Record<string, { survey: RegistrationSurveyCode; title: string; lede: string; press: string }>
> = {
  RIA_FIRM: {
    survey: 'RIA_FIRM',
    title: 'Your answers place your team on the RIA firm survey',
    lede: 'That survey registers the firm itself, by its SEC number. ',
    press: 'Register the firm'
  },
  TEAMS: {
    survey: 'TEAMS',
    title: 'Register your team instead',
    lede: 'The team survey registers a team by its lead advisor’s CRD number. ',
    press: 'Register the team'
  }
};
